import React, { useEffect, useMemo, useState } from "react";
import InputMask from "react-input-mask";
import { Search, Save } from "lucide-react";
import { listarDistribuidoresDropdown, listarRevendasDropdown, listarVendedoresTodos } from "../services/api";

const CadastroCliente = ({ token, initialData = {}, isEdit = false, onSubmit }) => {
  const [cliente, setCliente] = useState({
    cnpj: "",
    inscricao_estadual: "",
    razao_social: "",
    nome_fantasia: "",
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    cep: "",
    cidade: "",
    estado: "",
    email: "",
    telefone: "",
    simples_nacional: true,

    // ✅ NOVO: vendedor do cliente
    vendedor_id: "",
    revenda_id: "0",
    distribuidor_id: "",

    ...initialData,
  });

  const [loadingCnpj, setLoadingCnpj] = useState(false);

  // ✅ NOVO: vendedores dropdown
  const [vendedores, setVendedores] = useState([]);
  const [loadingVendedores, setLoadingVendedores] = useState(false);
  const [revendas, setRevendas] = useState([]);
  const [distribuidores, setDistribuidores] = useState([]);

  // ✅ NOVO: heurística simples:
  // se o initialData indicar RevendaId 0 => master => só vendedores da master
  const revendaIdDoCliente = useMemo(() => {
    // tente pegar de vários nomes possíveis
    const rid =
      initialData?.revendaId ??
      initialData?.RevendaId ??
      cliente?.revendaId ??
      cliente?.RevendaId;

    if (rid === 0 || rid === "0") return 0;
    return undefined; // undefined => deixa o backend usar o escopo do token (revenda)
  }, [initialData, cliente]);

  useEffect(() => {
    if (isEdit && initialData && Object.keys(initialData).length > 0) {
      setCliente({
        cnpj: initialData.cnpj || "",
        inscricao_estadual: initialData.inscricaoEstadual || "",
        razao_social: initialData.razaoSocial || "",
        nome_fantasia: initialData.nomeFantasia || "",
        logradouro: initialData.logradouro || "",
        numero: initialData.numero || "",
        complemento: initialData.complemento || "",
        bairro: initialData.bairro || "",
        cep: initialData.cep || "",
        cidade: initialData.cidade || "",
        estado: initialData.estado || "",
        email: initialData.email || "",
        telefone: initialData.telefone || "",
        simples_nacional: initialData.simplesNacional ?? false,

        // ✅ NOVO: preenche vendedor do cliente em edição
        vendedor_id:
          initialData.vendedorId === null || initialData.vendedorId === undefined
            ? ""
            : String(initialData.vendedorId),
        revenda_id:
          initialData.revendaId === null || initialData.revendaId === undefined
            ? "0"
            : String(initialData.revendaId),
        distribuidor_id:
          initialData.distribuidorId === null || initialData.distribuidorId === undefined
            ? ""
            : String(initialData.distribuidorId),
      });
    }
  }, [initialData, isEdit]);

  // ✅ NOVO: carregar vendedores (respeitando regra master vs revenda)
  useEffect(() => {
    const fetchVendedores = async () => {
      setLoadingVendedores(true);
      try {
        const list = await listarVendedoresTodos(token, {
          pageSize: 2000,
          sortBy: "nome",
          desc: false,
          search: "",
          // se revendaIdDoCliente === 0 => master => força revendaId=0
          // se undefined => não manda revendaId => backend aplica token
          revendaId: revendaIdDoCliente,
        });

        setVendedores(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error("Erro ao listar vendedores:", e);
        setVendedores([]);
      } finally {
        setLoadingVendedores(false);
      }
    };

    fetchVendedores();
  }, [token, revendaIdDoCliente]);

  useEffect(() => {
    listarRevendasDropdown(token)
      .then((r) => setRevendas(r.data?.items || []))
      .catch(() => setRevendas([]));
    listarDistribuidoresDropdown(token)
      .then((r) => setDistribuidores(Array.isArray(r.data) ? r.data : []))
      .catch(() => setDistribuidores([]));
  }, [token]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCliente((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      RazaoSocial: cliente.razao_social,
      NomeFantasia: cliente.nome_fantasia,
      CNPJ: cliente.cnpj,
      Logradouro: cliente.logradouro,
      Numero: cliente.numero,
      Bairro: cliente.bairro,
      Cidade: cliente.cidade,
      Estado: cliente.estado,
      CEP: cliente.cep,
      Email: cliente.email,
      Telefone: cliente.telefone,
      InscricaoEstadual: cliente.inscricao_estadual,
      SimplesNacional: cliente.simples_nacional,
      Complemento: cliente.complemento || null,

      // ✅ NOVO
      VendedorId: cliente.vendedor_id ? Number(cliente.vendedor_id) : null,
      RevendaId: Number(cliente.revenda_id || 0),
      DistribuidorId:
        Number(cliente.revenda_id || 0) > 0
          ? null
          : cliente.distribuidor_id
            ? Number(cliente.distribuidor_id)
            : null,
    };

    try {
      await onSubmit(payload, token);
    } catch (err) {
      const data = err?.response?.data;

      if (typeof data === "string") {
        alert(data);
        return;
      }

      const message =
        data?.message ||
        (data?.errors && Object.values(data.errors).flat().join("\n")) ||
        err.message ||
        "Erro ao salvar cliente.";

      alert(message);
      console.error(err);
    }
  };

  const handleBuscarCNPJ = async () => {
    const cnpj = (cliente.cnpj || "").replace(/\D/g, "");
    if (!cnpj || cnpj.length !== 14) {
      alert("Informe um CNPJ válido.");
      return;
    }

    setLoadingCnpj(true);
    try {
      const res = await fetch(`https://publica.cnpj.ws/cnpj/${cnpj}`);
      const data = await res.json();

      const est = data?.estabelecimento;

      setCliente((prev) => ({
        ...prev,
        razao_social: data?.razao_social || prev.razao_social || "",
        nome_fantasia: est?.nome_fantasia || prev.nome_fantasia || "",
        logradouro: est?.logradouro || prev.logradouro || "",
        numero: est?.numero || prev.numero || "",
        complemento: est?.complemento || prev.complemento || "",
        bairro: est?.bairro || prev.bairro || "",
        cep: est?.cep || prev.cep || "",
        cidade: est?.cidade?.nome || prev.cidade || "",
        estado: est?.estado?.sigla || prev.estado || "",
        email: est?.email || prev.email || "",
        telefone: est?.telefone1 || prev.telefone || "",
        inscricao_estadual:
          est?.inscricoes_estaduais?.[0]?.inscricao_estadual ||
          prev.inscricao_estadual ||
          "",
        simples_nacional: data?.simples?.simples === "Sim",
      }));
    } catch (err) {
      console.error("Erro ao buscar CNPJ:", err);
      alert("Erro ao buscar CNPJ. Tente novamente.");
    } finally {
      setLoadingCnpj(false);
    }
  };

  const inputClass = `
    w-full px-3 py-2.5 rounded-xl border outline-none transition
    zen-card border-gray-300 text-gray-900 placeholder:text-gray-400
    focus:ring-2 focus:ring-blue-500/60
    dark:bg-slate-950/40 dark:border-white/10 dark:text-white dark:placeholder:text-slate-400
    dark:focus:ring-blue-400/40
  `;

  const labelClass = "block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1";

  return (
    <div className="max-w-5xl mx-auto">
      <div className="zen-card rounded-2xl p-6 md:p-8 shadow-sm dark:backdrop-blur">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
              {isEdit ? "Editar Cliente" : "Cadastro de Cliente"}
            </h2>
            <p className="text-sm text-gray-500 dark:text-slate-300/80 mt-1">
              Preencha os dados para {isEdit ? "editar o" : "cadastrar um novo"} cliente.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* bloco 1 */}
          <div className="zen-card rounded-2xl p-4 dark:bg-white/5 dark:border-white/10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-1">
                <label className={labelClass}>CNPJ</label>
                <div className="flex">
                  <InputMask
                    mask="99.999.999/9999-99"
                    name="cnpj"
                    value={cliente.cnpj}
                    onChange={handleChange}
                    className={`${inputClass} rounded-r-none`}
                    required
                  />
                  <button
                    onClick={handleBuscarCNPJ}
                    type="button"
                    disabled={loadingCnpj}
                    className={`
                      px-4 py-2.5 rounded-r-xl font-semibold transition
                      bg-blue-600 text-white hover:bg-blue-500
                      disabled:opacity-60 disabled:cursor-not-allowed
                      inline-flex items-center justify-center
                    `}
                    title="Buscar dados pelo CNPJ"
                  >
                    <Search size={18} />
                  </button>
                </div>
                {loadingCnpj && (
                  <p className="text-xs mt-1 text-gray-500 dark:text-slate-300/70">
                    Buscando dados do CNPJ...
                  </p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className={labelClass}>Inscrição Estadual</label>
                <input
                  name="inscricao_estadual"
                  value={cliente.inscricao_estadual}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>

              <div className="md:col-span-3">
                <label className={labelClass}>Razão Social</label>
                <input
                  name="razao_social"
                  value={cliente.razao_social}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>

              <div className="md:col-span-3">
                <label className={labelClass}>Nome Fantasia</label>
                <input
                  name="nome_fantasia"
                  value={cliente.nome_fantasia}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>
            </div>
          </div>

          {/* bloco 2 */}
          <div className="zen-card rounded-2xl p-4 dark:bg-white/5 dark:border-white/10">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-3">
                <label className={labelClass}>Logradouro</label>
                <input
                  name="logradouro"
                  value={cliente.logradouro}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>

              <div className="md:col-span-1">
                <label className={labelClass}>Número</label>
                <input
                  name="numero"
                  value={cliente.numero}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className={labelClass}>Complemento</label>
                <input
                  name="complemento"
                  value={cliente.complemento}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>

              <div className="md:col-span-2">
                <label className={labelClass}>Bairro</label>
                <input
                  name="bairro"
                  value={cliente.bairro}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>

              <div className="md:col-span-1">
                <label className={labelClass}>CEP</label>
                <InputMask
                  mask="99999-999"
                  name="cep"
                  value={cliente.cep}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className={labelClass}>Cidade</label>
                <input
                  name="cidade"
                  value={cliente.cidade}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>

              <div className="md:col-span-1">
                <label className={labelClass}>Estado</label>
                <input
                  name="estado"
                  value={cliente.estado}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>
            </div>
          </div>

          {/* bloco 3 */}
          <div className="zen-card rounded-2xl p-4 dark:bg-white/5 dark:border-white/10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Email</label>
                <input
                  type="email"
                  name="email"
                  value={cliente.email}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>Telefone</label>
                <InputMask
                  mask="(99) 99999-9999"
                  name="telefone"
                  value={cliente.telefone}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>Revenda</label>
                <select
                  name="revenda_id"
                  value={cliente.revenda_id || "0"}
                  onChange={(e) => {
                    const revendaId = e.target.value;
                    setCliente((prev) => ({
                      ...prev,
                      revenda_id: revendaId,
                      distribuidor_id: revendaId !== "0" ? "" : prev.distribuidor_id,
                    }));
                  }}
                  className={inputClass}
                >
                  <option value="0">Cliente direto (sem revenda)</option>
                  {revendas.map((r) => (
                    <option key={r.id} value={String(r.id)}>
                      {r.nomeFantasia || r.razaoSocial}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Distribuidor</label>
                <select
                  name="distribuidor_id"
                  value={cliente.distribuidor_id || ""}
                  onChange={handleChange}
                  disabled={String(cliente.revenda_id || "0") !== "0"}
                  className={inputClass}
                >
                  <option value="">Zen (sem distribuidor)</option>
                  {distribuidores.map((d) => (
                    <option key={d.id} value={String(d.id)}>
                      {d.nomeFantasia || d.razaoSocial}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className={labelClass}>Vendedor</label>
                <select
                  name="vendedor_id"
                  value={cliente.vendedor_id}
                  onChange={handleChange}
                  disabled={loadingVendedores}
                  className={inputClass}
                >
                  <option value="">(Sem vendedor)</option>
                  {vendedores.map((v) => (
                    <option key={v.id} value={String(v.id)}>
                      {v.nome}
                    </option>
                  ))}
                </select>

                {loadingVendedores && (
                  <p className="text-xs mt-1 text-gray-500 dark:text-slate-300/70">
                    Carregando vendedores...
                  </p>
                )}
              </div>

              <div className="md:col-span-2 flex items-center gap-3 pt-1">
                <span className="text-sm font-medium text-gray-700 dark:text-slate-200">
                  Simples Nacional
                </span>

                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    name="simples_nacional"
                    checked={!!cliente.simples_nacional}
                    onChange={handleChange}
                    className="
                      h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500
                      dark:border-white/20 dark:bg-slate-950/40
                    "
                  />
                  <span className="text-sm text-gray-700 dark:text-slate-200">Sim</span>
                </label>
              </div>
            </div>
          </div>

          {/* actions */}
          <div className="flex justify-end pt-2">
            <button
              type="submit"
              className="
                inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition
                bg-blue-600 text-white hover:bg-blue-500
              "
            >
              <Save size={18} />
              {isEdit ? "Salvar Alterações" : "Cadastrar Cliente"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CadastroCliente;
